if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this._mo = new MutationObserver(() => this._refreshElements());
        this._mo.observe(this, { childList: true, subtree: true });

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          this._onSlideChangedDebounced = debounce(this.onSlideChanged.bind(this), 500);
          this.elements.viewer.addEventListener('slideChanged', this._onSlideChangedDebounced);
        }

        if (this.elements.thumbnails) {
          this._onThumbClick = (e) => {
            const li = e.target.closest('[data-target]');
            if (!li || !this.elements.thumbnails.contains(li)) return;
            e.preventDefault();
            this.setActiveMedia(li.dataset.target, false);
          };
          this.elements.thumbnails.addEventListener('click', this._onThumbClick);

          this._onThumbScroll = () => {
            this._thumbsUserScrolling = true;
            this._updateThumbButtons();
            clearTimeout(this._thumbsScrollEndT);
            this._thumbsScrollEndT = setTimeout(() => (this._thumbsUserScrolling = false), 140);
          };
          this.elements.thumbnails.slider?.addEventListener('scroll', this._onThumbScroll, { passive: true });

          this._bindArrowButtons();
        }

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this._onResize = debounce(() => {
          this._cacheSlideMetrics(true);
          this._updateThumbButtons();
        }, 120);
        window.addEventListener('resize', this._onResize);

        requestAnimationFrame(() => {
          this._cacheSlideMetrics(true);
          this._updateThumbButtons();
        });
      }

      _cacheSlideMetrics(force = false) {
        const track = this.elements?.thumbnails?.slider;
        if (!track) return;

        if (this._metricsCached && !force) return;
        this._metricsCached = true;

        const first = track.querySelector('.slider__slide');
        if (!first) return;

        const cs = getComputedStyle(first);
        const ml = parseFloat(cs.marginLeft || '0');
        const mr = parseFloat(cs.marginRight || '0');
        const w = first.getBoundingClientRect().width;
        this._slideStep = w + ml + mr;
        this._eps = 16;
      }

      _bindArrowButtons() {
        const wrap = this.elements.thumbnails;
        if (!wrap) return;
        this._onPrev = (e) => {
          e.preventDefault();
          this._cacheSlideMetrics();
          const scroller = wrap.slider;
          if (!scroller) return;
          const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
          const step = parseInt(e.currentTarget.dataset.step || '3', 10);
          const delta = this._slideStep * step;
          const nextLeft = Math.max(0, scroller.scrollLeft - delta);
          scroller.scrollTo({ left: nextLeft, behavior: 'smooth' });
        };
        this._onNext = (e) => {
          e.preventDefault();
          this._cacheSlideMetrics();
          const scroller = wrap.slider;
          if (!scroller) return;
          const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
          const step = parseInt(e.currentTarget.dataset.step || '3', 10);
          const delta = this._slideStep * step;
          const remaining = max - scroller.scrollLeft;
          const nextLeft = remaining <= delta ? max : Math.min(max, scroller.scrollLeft + delta);
          scroller.scrollTo({ left: nextLeft, behavior: 'smooth' });
        };

        wrap.querySelector('.slider-button--prev')?.addEventListener('click', this._onPrev);
        wrap.querySelector('.slider-button--next')?.addEventListener('click', this._onNext);
      }

      _scrollThumbIntoView(thumbnail) {
        const scroller = this.elements?.thumbnails?.slider;
        if (!scroller || !thumbnail) return;

        const edge = 8;
        const targetLeft = thumbnail.offsetLeft;
        const targetRight = targetLeft + thumbnail.offsetWidth;

        const viewLeft = scroller.scrollLeft;
        const viewRight = viewLeft + scroller.clientWidth;

        if (targetLeft < viewLeft + edge) {
          scroller.scrollTo({ left: Math.max(0, targetLeft - edge), behavior: 'smooth' });
        } else if (targetRight > viewRight - edge) {
          scroller.scrollTo({ left: targetRight - scroller.clientWidth + edge, behavior: 'smooth' });
        }
      }

      onSlideChanged(event) {
        const current = event?.detail?.currentElement;
        const mediaId = current?.dataset?.mediaId;
        if (!mediaId || !this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const viewer = this.elements.viewer;
        if (!viewer) return;

        const activeMedia =
          viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        viewer.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement?.firstChild !== activeMedia) {
            activeMedia.parentElement?.prepend(activeMedia);
          }
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail?.parentElement?.firstChild !== activeThumbnail) {
              activeThumbnail?.parentElement?.prepend(activeThumbnail);
            }
          }
          viewer.slider?.resetPages?.();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || !this.elements.thumbnails) {
            activeMedia.parentElement?.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
          const rect = activeMedia.getBoundingClientRect();
          if (rect.top <= -0.5) {
            window.scrollTo({ top: rect.top + window.scrollY, behavior: 'smooth' });
          }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail?.dataset?.mediaPosition) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((el) => el.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');

        this._scrollThumbIntoView(thumbnail);

        requestAnimationFrame(() => this._updateThumbButtons());
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;

        const apply = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', 'false');
          this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', 'true'), 2000);
        };

        if (image.complete) apply();
        else image.addEventListener('load', apply, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        deferredMedia?.loadContent?.(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        this.stickyHeader?.dispatchEvent?.(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems?.forEach?.((slide) => slide.setAttribute('role', 'presentation'));
      }

      _refreshElements() {
        const newViewer = this.querySelector('[id^="GalleryViewer"]');
        const newThumbs = this.querySelector('[id^="GalleryThumbnails"]');

        if (newViewer && newViewer !== this.elements.viewer) {
          this.elements.viewer?.removeEventListener?.('slideChanged', this._onSlideChangedDebounced);
          this.elements.viewer = newViewer;
          this._onSlideChangedDebounced = debounce(this.onSlideChanged.bind(this), 500);
          this.elements.viewer.addEventListener('slideChanged', this._onSlideChangedDebounced);
        }

        if (newThumbs && newThumbs !== this.elements.thumbnails) {
          this.elements.thumbnails?.removeEventListener?.('click', this._onThumbClick);
          this.elements.thumbnails?.slider?.removeEventListener?.('scroll', this._onThumbScroll);
          this.elements.thumbnails?.querySelector('.slider-button--prev')?.removeEventListener('click', this._onPrev);
          this.elements.thumbnails?.querySelector('.slider-button--next')?.removeEventListener('click', this._onNext);

          this.elements.thumbnails = newThumbs;
          this.elements.thumbnails.addEventListener('click', this._onThumbClick);
          this.elements.thumbnails.slider?.addEventListener('scroll', this._onThumbScroll, { passive: true });
          this._bindArrowButtons();

          if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

          requestAnimationFrame(() => {
            this._cacheSlideMetrics(true);
            this._updateThumbButtons();
          });
        }
      }

      disconnectedCallback() {
        this._mo?.disconnect?.();
        this.elements.viewer?.removeEventListener?.('slideChanged', this._onSlideChangedDebounced);
        this.elements.thumbnails?.removeEventListener?.('click', this._onThumbClick);
        this.elements.thumbnails?.slider?.removeEventListener?.('scroll', this._onThumbScroll);
        this.elements.thumbnails?.querySelector('.slider-button--prev')?.removeEventListener('click', this._onPrev);
        this.elements.thumbnails?.querySelector('.slider-button--next')?.removeEventListener('click', this._onNext);
        window.removeEventListener('resize', this._onResize);
      }

      _updateThumbButtons() {
        const scroller = this.elements?.thumbnails?.slider;
        if (!scroller) return;

        const prev = this.elements.thumbnails.querySelector('.slider-button--prev');
        const next = this.elements.thumbnails.querySelector('.slider-button--next');
        if (!prev || !next) return;

        const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
        const eps = this._eps ?? 16;
        const atStart = scroller.scrollLeft <= eps;
        const atEnd = maxScrollLeft - scroller.scrollLeft <= eps;

        prev.toggleAttribute('disabled', atStart);
        next.toggleAttribute('disabled', atEnd);
      }
    }
  );
}